import { FileText, Terminal, RefreshCw, MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

interface Bot {
  id: string;
  name: string;
  status: 'healthy' | 'warning' | 'error' | 'offline';
  uptime: number;
  memory: number;
  cpu: number;
  requests: number;
  version?: string;
  environment?: string;
  lastDeploy?: string;
}

interface BotTechnicalTableProps {
  bots: Bot[];
  onSelect: (botId: string) => void;
  selectedId: string | null;
}

const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
};

const formatDate = (dateString?: string): string => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getStatusConfig = (status: Bot['status']) => {
  const configs = {
    healthy: { color: 'bg-green-500', text: 'Healthy', variant: 'default' },
    warning: { color: 'bg-yellow-500', text: 'Warning', variant: 'secondary' },
    error: { color: 'bg-red-500', text: 'Error', variant: 'destructive' },
    offline: { color: 'bg-gray-500', text: 'Offline', variant: 'outline' }
  };
  return configs[status];
};

const getEnvironmentVariant = (environment?: string) => {
  const variants = {
    production: 'default',
    staging: 'secondary',
    development: 'outline'
  };
  return environment ? variants[environment as keyof typeof variants] || 'outline' : 'outline';
};

const getMetricStatus = (value: number, type: 'uptime' | 'memory' | 'cpu') => {
  switch (type) {
    case 'uptime':
      if (value > 99) return 'text-green-600';
      if (value > 95) return 'text-yellow-600';
      return 'text-red-600';
    case 'memory':
      if (value < 500) return 'text-green-600';
      if (value < 800) return 'text-yellow-600';
      return 'text-red-600';
    case 'cpu':
      if (value < 70) return 'text-green-600';
      if (value < 90) return 'text-yellow-600';
      return 'text-red-600';
    default:
      return '';
  }
};

export function BotTechnicalTable({ bots, onSelect, selectedId }: BotTechnicalTableProps) {
  const viewLogs = (botId: string) => {
    console.log('Viewing logs for bot:', botId);
  };

  const openConsole = (botId: string) => {
    console.log('Opening console for bot:', botId);
  };

  const restartBot = (botId: string) => {
    console.log('Restarting bot:', botId);
  };

  const deployBot = (botId: string) => {
    console.log('Deploying bot:', botId);
  };

  return (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[200px]">Bot Name</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Environment</TableHead>
            <TableHead>Version</TableHead>
            <TableHead className="text-right">Uptime</TableHead>
            <TableHead className="text-right">Memory</TableHead>
            <TableHead className="text-right">CPU</TableHead>
            <TableHead className="text-right">Requests</TableHead>
            <TableHead>Last Deploy</TableHead>
            <TableHead className="w-[100px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {bots.map((bot) => {
            const statusConfig = getStatusConfig(bot.status);
            
            return (
              <TableRow 
                key={bot.id}
                className={cn(
                  "cursor-pointer hover:bg-gray-50 transition-colors",
                  selectedId === bot.id && "bg-blue-50 border-blue-200"
                )}
                onClick={() => onSelect(bot.id)}
              >
                <TableCell className="font-medium">
                  <div>
                    <div className="font-medium text-sm">{bot.name}</div>
                    <div className="text-xs text-muted-foreground font-mono">
                      {bot.id}
                    </div>
                  </div>
                </TableCell>
                
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className={cn('w-2 h-2 rounded-full', statusConfig.color)} />
                    <Badge variant={statusConfig.variant as any} className="text-xs">
                      {statusConfig.text}
                    </Badge>
                  </div>
                </TableCell>
                
                <TableCell>
                  {bot.environment && (
                    <Badge variant={getEnvironmentVariant(bot.environment) as any} className="text-xs">
                      {bot.environment}
                    </Badge>
                  )}
                </TableCell>
                
                <TableCell>
                  <span className="font-mono text-xs">{bot.version || 'N/A'}</span>
                </TableCell>
                
                <TableCell className="text-right">
                  <span className={cn('font-mono text-sm', getMetricStatus(bot.uptime, 'uptime'))}>
                    {bot.uptime}%
                  </span>
                </TableCell>
                
                <TableCell className="text-right">
                  <span className={cn('font-mono text-sm', getMetricStatus(bot.memory, 'memory'))}>
                    {bot.memory}MB
                  </span>
                </TableCell>
                
                <TableCell className="text-right">
                  <span className={cn('font-mono text-sm', getMetricStatus(bot.cpu, 'cpu'))}>
                    {bot.cpu}%
                  </span>
                </TableCell>
                
                <TableCell className="text-right">
                  <span className="font-mono text-sm">
                    {formatNumber(bot.requests)}
                  </span>
                </TableCell>
                
                <TableCell>
                  <span className="text-xs text-muted-foreground">
                    {formatDate(bot.lastDeploy)}
                  </span>
                </TableCell>
                
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        viewLogs(bot.id);
                      }}
                      title="View Logs"
                    >
                      <FileText className="w-3 h-3" />
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        openConsole(bot.id);
                      }}
                      title="Open Console"
                    >
                      <Terminal className="w-3 h-3" />
                    </Button>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreHorizontal className="w-3 h-3" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => restartBot(bot.id)}>
                          <RefreshCw className="w-3 h-3 mr-2" />
                          Restart
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => deployBot(bot.id)}>
                          Deploy
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          Configure
                        </DropdownMenuItem>
                        <DropdownMenuItem className="text-red-600">
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}